import { useMemo, useRef, useState } from 'react';

import { useClickOutside } from '@/hooks/useClickOutside';

export function MultiSelect({
  emptyLabel,
  formatOption = (option) => option,
  label,
  onChange,
  options,
  selected,
}: {
  emptyLabel: string;
  formatOption?: (option: string) => string;
  label: string;
  onChange: (next: string[]) => void;
  options: readonly string[];
  selected: string[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useClickOutside(ref, () => setOpen(false));

  function toggle(option: string) {
    onChange(selectedSet.has(option) ? selected.filter((item) => item !== option) : [...selected, option]);
  }

  return (
    <div ref={ref} className='relative' onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}>
      <button
        type='button'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className='flex min-h-9 min-w-[150px] items-center gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700 hover:border-neutral-300'
      >
        <span className='text-neutral-500'>{label}</span>
        <span className='font-mediumbold max-w-[180px] truncate text-neutral-900'>
          {selected.length > 0 ? selected.map(formatOption).join(', ') : emptyLabel}
        </span>
        <svg
          className='ml-auto size-4 shrink-0 text-neutral-400'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
          viewBox='0 0 24 24'
        >
          <path d='m6 9 6 6 6-6' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </button>
      {open && (
        <div
          role='listbox'
          aria-multiselectable
          className='absolute right-0 z-20 mt-1 min-w-full rounded-md border border-neutral-200 bg-white py-1 shadow-lg'
        >
          <button
            type='button'
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
            className='w-full px-3 py-1.5 text-left text-sm text-neutral-600 hover:bg-neutral-50'
          >
            {emptyLabel}
          </button>
          {options.map((option) => (
            <button
              key={option}
              type='button'
              role='option'
              aria-selected={selectedSet.has(option)}
              onClick={() => toggle(option)}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
                selectedSet.has(option) ? 'bg-neutral-50 font-medium text-neutral-900' : 'text-neutral-600'
              }`}
            >
              <span
                className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                  selectedSet.has(option) ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300'
                }`}
              >
                {selectedSet.has(option) && (
                  <svg className='size-3' fill='none' stroke='currentColor' strokeWidth={3} viewBox='0 0 24 24'>
                    <path d='m5 13 4 4L19 7' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                )}
              </span>
              {formatOption(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
